import type { ClarifyAnswer, ClarifyQuestion } from "@prelude-ai/prelude-agentic-framework"
import type { SSEEvent } from "../../types/index.ts"
import type { TranscriptResponse } from "../../utils/conversation-store.ts"
import type { ChatMessage, StepInfo, TurnInfo } from "../types.ts"

/**
 * Creates a new turn info object from an SSE event
 */
export function createTurnFromEvent(event: SSEEvent): TurnInfo {
  return {
    symbol: event.symbol || "",
    response: event.response || "",
    timestamp: new Date(),
    turnType: event.turnType,
    toolCalls: event.toolCalls,
    toolName: event.toolName,
    toolArguments: event.toolArguments,
    toolCallId: event.toolCallId,
    startedAt: event.startedAt,
    finishedAt: event.finishedAt,
    isError: event.isError,
    httpTraces: event.httpTraces
  }
}

/**
 * Updates step history with a new turn event
 */
export function updateStepHistory(prev: StepInfo[], event: SSEEvent): StepInfo[] {
  if (!event.stepName) return prev

  const currentStep = event.stepName
  const isTransition = event.isTransition || false
  const previousStep = event.previousStepName
  const newTurn = createTurnFromEvent(event)

  // Find if we already have this step group
  const existingGroupIndex = prev.findIndex((group) => group.stepName === currentStep)
  const existingGroup = prev[existingGroupIndex]

  if (existingGroup) {
    // Add turn to existing step group (prepend to turns array for reverse display)
    const updated = [...prev]
    updated[existingGroupIndex] = {
      ...existingGroup,
      turns: [newTurn, ...existingGroup.turns]
    }
    return updated
  } else {
    // Create new step group and prepend to array (so it appears at top)
    const newGroup: StepInfo = {
      stepName: currentStep,
      turns: [newTurn],
      isTransition: isTransition,
      previousStepName: previousStep
    }
    return [newGroup, ...prev]
  }
}

/**
 * Updates step path from an SSE event
 */
export function updateStepPath(prev: string[], event: SSEEvent): string[] {
  if (event.stepHistory && event.stepHistory.length > 0) {
    return event.stepHistory
  } else if (event.stepName) {
    // Fallback: add current step to path if not already there
    if (prev[prev.length - 1] !== event.stepName) {
      return [...prev, event.stepName]
    }
  }
  return prev
}

/**
 * Parses SSE buffer and extracts complete events
 * Returns the parsed events and remaining buffer
 */
export function parseSSEBuffer(buffer: string): { events: SSEEvent[]; remaining: string } {
  const events: SSEEvent[] = []
  const lines = buffer.split("\n\n")
  const remaining = lines.pop() || ""

  for (const line of lines) {
    if (line.trim() && line.startsWith("data: ")) {
      const event: SSEEvent = JSON.parse(line.slice(6))
      events.push(event)
    }
  }

  return { events, remaining }
}

/**
 * Creates a message object
 */
export function createMessage(role: "user" | "assistant" | "system", content: string, id?: string): ChatMessage {
  return {
    id: id || `${role}-${Date.now()}-${Math.random()}`,
    role,
    content,
    timestamp: new Date()
  }
}

/**
 * Determines if intermediate response should be saved as a message
 */
export function shouldSaveIntermediateResponse(
  prevResponse: string,
  newResponse: string,
  lastAddedMessage: string
): boolean {
  return Boolean(prevResponse && prevResponse !== newResponse && prevResponse !== lastAddedMessage)
}

/**
 * Derives devtools panel state (stepHistory, stepPath, flowState) from transcript entries.
 * Used when loading a conversation to restore the devtools panel state that is
 * normally only populated via live SSE events.
 *
 * Reads the canonical per-turn `flowContext` snapshot. The retired entry-level
 * `flowState` lane is gone — records carrying it are rejected at the load
 * boundary, so this can never receive one. The returned field stays named
 * `flowState` because that is what the devtools reducer state exposes to the UI.
 */
export function deriveDevToolsStateFromTranscript(transcript: TranscriptResponse["transcript"]): {
  stepHistory: StepInfo[]
  stepPath: string[]
  flowState: Record<string, unknown>
} {
  const stepHistory: StepInfo[] = []
  const stepPath: string[] = []
  let flowState: Record<string, unknown> = {}

  const isNonEmptyRecord = (obj: unknown): obj is Record<string, unknown> =>
    !!obj && typeof obj === "object" && !Array.isArray(obj) && Object.keys(obj as Record<string, unknown>).length > 0

  for (const entry of transcript) {
    if (entry.flowStepName) {
      const stepName = entry.flowStepName

      if (stepPath[stepPath.length - 1] !== stepName) {
        stepPath.push(stepName)
      }

      const turn: TurnInfo = {
        symbol: entry.symbol || "",
        response: entry.response || "",
        timestamp: entry.startedAt ? new Date(entry.startedAt) : new Date(),
        turnType: entry.type,
        toolName: entry.toolName,
        toolArguments: entry.toolArguments,
        toolCallId: entry.toolCallId,
        startedAt: entry.startedAt,
        finishedAt: entry.finishedAt,
        isError: entry.isError
      }

      const existing = stepHistory.find((g) => g.stepName === stepName)
      if (existing) {
        existing.turns = [turn, ...existing.turns]
      } else {
        stepHistory.unshift({
          stepName,
          turns: [turn],
          isTransition: entry.isTransition || false,
          previousStepName: entry.previousFlowStepName
        })
      }
    }

    const flowContextOnEntry = (entry as { flowContext?: unknown }).flowContext
    if (isNonEmptyRecord(flowContextOnEntry)) {
      flowState = flowContextOnEntry
    }
  }

  return { stepHistory, stepPath, flowState }
}

/**
 * Returns true for messages that represent agent reasoning — tool calls, "Calling tools:" placeholders,
 * and assistant turns with a non-respond/non-pause symbol. Respond and pause are user-facing steps,
 * not internal reasoning.
 */
export function isReasoningMessage(msg: ChatMessage): boolean {
  if (/^Calling tools?:\s*\w+/.test(msg.content) && msg.role === "assistant") {
    return true
  }
  if (msg.isToolCall && msg.toolName) {
    return true
  }
  if (msg.role === "assistant" && msg.symbol && msg.symbol !== "respond" && msg.symbol !== "pause") {
    return true
  }
  return false
}

const CLARIFY_TOOL_NAME = "clarifyWithUser"

function clarificationSummaryFromEntry(entry: TranscriptResponse["transcript"][number]): {
  questions: string[]
  answers: (string | null)[]
} {
  const questions = ((entry.toolArguments as { questions?: ClarifyQuestion[] } | undefined)?.questions ?? []).map(
    (question) => question.message
  )
  const answers = ((entry as { toolResult?: { answers?: ClarifyAnswer[] } }).toolResult?.answers ?? []).map((answer) =>
    answer.skipped ? null : (answer.message ?? null)
  )
  return { questions, answers }
}

export function transcriptToChatMessages(transcript: TranscriptResponse["transcript"]): ChatMessage[] {
  return transcript
    .filter(
      (entry) =>
        entry.type === "user_input" ||
        entry.type === "assistant" ||
        (entry.type === "tool_response" && entry.toolName === CLARIFY_TOOL_NAME)
    )
    .filter((entry) => entry.type === "tool_response" || (entry.response && entry.response.trim() !== ""))
    .map((entry, index) => {
      const id = `transcript-${index}-${entry.startedAt}`
      const timestamp = entry.startedAt ? new Date(entry.startedAt) : new Date()

      if (entry.type === "tool_response") {
        return {
          id,
          role: "user" as const,
          content: "",
          timestamp,
          clarificationSummary: clarificationSummaryFromEntry(entry)
        }
      }

      return {
        id,
        role: entry.type === "user_input" ? ("user" as const) : ("assistant" as const),
        content: entry.response || "",
        timestamp,
        toolName: entry.toolName,
        toolArguments: entry.toolArguments,
        toolCallId: entry.toolCallId,
        isToolCall: Boolean(entry.toolName),
        symbol: entry.symbol
      }
    })
}
